import React, {useLayoutEffect, useState} from 'react';

import Mousetrap, {ExtendedKeyboardEvent} from 'mousetrap';

import {gt} from 'app/utils/i18n';

Mousetrap.prototype.stopCallback = function (
  e: ExtendedKeyboardEvent,
  element: HTMLElement
) {
  if (e.keyCode === 27) {
    return false;
  }

  // if the element has the class "mousetrap" then no need to stop
  if ((' ' + element.className + ' ').indexOf(' mousetrap ') > -1) {
    return false;
  }

  // stop for input, select, and textarea
  return (
    element.tagName === 'INPUT' ||
    element.tagName === 'SELECT' ||
    element.tagName === 'TEXTAREA' ||
    (element.contentEditable && element.contentEditable === 'true')
  );
};

export const categories = [
  {
    slug: 'everywhere',
    name: (): string => gt('Everywhere'),
    items: [],
  },
  {
    slug: 'detailPage',
    name: (): string => gt('Detail Page'),
    items: [],
  },
  {
    slug: 'editPage',
    name: (): string => gt('Edit Page'),
    items: [],
  },
];

export const cats: {[category: string]: string} = categories.reduce(
  (obj, cat) => {
    obj[cat.slug] = cat.slug;
    return obj;
  },
  {}
);

export type EventCallBackFuncType = (e: ExtendedKeyboardEvent) => void;

export interface RegisteredShortcutType {
  name: () => string;
  category: string;
  enabled: boolean;
}

export interface ShortcutToRegisterType {
  key: string;
  name: () => string;
  category: string;
}

interface ShortcutToRegisterAndEnableType {
  key: string;
  name: () => string;
  category: string;
  callback: EventCallBackFuncType;
}

export interface KeyboardShortcutsContextType {
  registerShortcut: (shortcut: ShortcutToRegisterType) => void;
  enableShortcut: (key: string, callback: EventCallBackFuncType) => void;
  disableShortcut: (key: string) => void;
  registerAndEnableShortcuts: (
    shortcuts: Array<ShortcutToRegisterAndEnableType>
  ) => void;
  shortcuts: Record<string, RegisteredShortcutType>;
}

export const KeyboardShortcutsContext =
  React.createContext<KeyboardShortcutsContextType>({
    registerShortcut: () => undefined,
    enableShortcut: () => undefined,
    disableShortcut: () => undefined,
    registerAndEnableShortcuts: () => undefined,
    shortcuts: {},
  });

export let shortcutsToRegister: Array<ShortcutToRegisterType> = [];

interface Props {
  children: React.ReactNode;
}

export function KeyboardShortcutsProvider({children}: Props): JSX.Element {
  const [shortcuts, setShortcuts] = useState<
    Record<string, RegisteredShortcutType>
  >({});

  useLayoutEffect(() => {
    shortcutsToRegister.map(registerShortcut);
  }, []);

  const enableShortcut = (key: string, callback: EventCallBackFuncType) => {
    setShortcuts((shortcuts) => ({
      ...shortcuts,
      [key]: {...shortcuts[key], enabled: true},
    }));
    Mousetrap.bind(key, callback);
  };

  const disableShortcut = (key: string) => {
    setShortcuts((shortcuts) => ({
      ...shortcuts,
      [key]: {...shortcuts[key], enabled: false},
    }));
    Mousetrap.unbind(key);
  };

  const registerShortcut = ({key, name, category}: ShortcutToRegisterType) => {
    setShortcuts((shortcuts) => ({
      ...shortcuts,
      [key]: {
        name,
        category,
        enabled: false,
      },
    }));
  };

  // this one is special for NavigationTabs component
  // that component requires registering and enabling several shortcuts on mount
  const registerAndEnableShortcuts = (
    shortcuts: Array<ShortcutToRegisterAndEnableType>
  ) => {
    setShortcuts((currShortcuts) => ({
      ...currShortcuts,
      ...shortcuts.reduce<Record<string, RegisteredShortcutType>>(
        (dict, shortcut) => {
          const {key, name, category} = shortcut;
          dict[key] = {
            name,
            category,
            enabled: true,
          };
          return dict;
        },
        {}
      ),
    }));
    shortcuts.forEach(({key, callback}) => Mousetrap.bind(key, callback));
  };

  const value: KeyboardShortcutsContextType = {
    registerShortcut,
    enableShortcut,
    disableShortcut,
    shortcuts,
    registerAndEnableShortcuts,
  };
  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
}
